import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";
import ChartistGraph from "react-chartist";

import classNames from "classnames";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #0d1b6e 0%, #1a237e 60%, #1565c0 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "30px 40px",
    display: "flex",
    flexDirection: "column",
    color: "#fff"
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    color: "rgba(255,255,255,0.8)",
    textDecoration: "none",
    fontSize: "13px",
    marginBottom: "20px",
    cursor: "pointer",
    transition: "color 0.2s",
    "&:hover": { color: "#fff" }
  },
  heroTitle: { fontSize: "28px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "14px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },
  coursePicker: {
    background: "rgba(255,255,255,0.15)",
    backdropFilter: "blur(10px)",
    borderRadius: "12px",
    padding: "4px 16px",
    border: "1px solid rgba(255,255,255,0.2)",
    color: "#fff",
    "& select": { color: "#fff" },
    "& label": { color: "rgba(255,255,255,0.7)" },
    "& .MuiInput-underline:before": { borderBottomColor: "rgba(255,255,255,0.3)" },
    "& .MuiInput-underline:after": { borderBottomColor: "#fff" },
    "& .MuiSelect-icon": { color: "#fff" }
  },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginTop: "-80px", marginBottom: "32px", padding: "0 20px", zIndex: 3, position: "relative" },
  statCard: {
    flex: 1,
    minWidth: "180px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    border: "1px solid #f1f5f9",
  },
  statHeader: { display: "flex", alignItems: "center", gap: "12px" },
  iconBox: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "24px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: 0, fontWeight: 500 },
  statMeta: { fontSize: "11px", color: "#94a3b8", marginTop: -4 },
  progressBar: { height: "4px", width: "100%", background: "#f1f5f9", borderRadius: "2px", overflow: "hidden" },
  progressFill: { height: "100%", background: "#3b82f6" },

  tableActions: { display: "flex", gap: "8px" },
  actionBtn: {
    padding: "8px 16px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  saveBtn: { background: "#2563eb", color: "#fff", "&:hover": { background: "#1d4ed8" } },
  outlineBtn: { border: "1px solid #e2e8f0", background: "#fff", color: "#475569", "&:hover": { background: "#f8fafc" } },

  scoreInput: {
    width: "80px",
    height: "36px",
    padding: "0 10px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    textAlign: "center",
    fontSize: "14px",
    transition: "all 0.2s",
    "&:focus": { outline: "none", borderColor: "#3b82f6", boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)" }
  },

  // Bottom sections
  bottomGrid: { marginTop: "24px" },
  sectionCard: {
    background: "#fff",
    borderRadius: "16px",
    border: "1px solid #f1f5f9",
    padding: "24px",
    height: "100%",
    boxSizing: "border-box"
  },
  sectionTitle: { fontSize: "15px", fontWeight: 700, color: "#1e293b", marginBottom: "20px" },
  
  // Grade distribution
  distRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px", fontSize: "13px" },
  distLabel: { display: "flex", alignItems: "center", gap: "8px", color: "#64748b" },
  dot: { width: "8px", height: "8px", borderRadius: "50%" },
  distValue: { fontWeight: 600, color: "#1e293b" },
  
  // Quick Actions
  quickGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  quickItem: {
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid #f1f5f9",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": { background: "#f8fafc", borderColor: "#e2e8f0" }
  },
  quickIcon: { color: "#6366f1", fontSize: "20px" },
  quickTitle: { fontSize: "13px", fontWeight: 600, color: "#1e293b" },
  quickDesc: { fontSize: "11px", color: "#94a3b8" },

  // Grade scale
  scaleItem: { display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #f8fafc", fontSize: "13px" },

  errorMsg: { color: "#ef4444", background: "#fee2e2", padding: "12px", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" },
  successMsg: { color: "#10b981", background: "#d1fae5", padding: "12px", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" },
};

function calcGrade(score) {
  if (score >= 80) return { grade: "A", tone: "success", label: "Excellent" };
  if (score >= 70) return { grade: "B", tone: "info", label: "Very Good" };
  if (score >= 60) return { grade: "C", tone: "muted", label: "Average" };
  if (score >= 50) return { grade: "D", tone: "warning", label: "Below Average" };
  return { grade: "F", tone: "danger", label: "Fail" };
}

class CourseworkMarks extends Component {
  state = {
    courses: [], 
    selectedCourse: "", 
    enrollments: [], 
    marks: {},
    loading: false, 
    saving: false,
    error: null, 
    success: null,
    searchValue: ""
  };

  componentDidMount() { this.fetchCourses(); }

  fetchCourses = async () => {
    try {
      const res = await api.get("/courses?limit=100");
      const d = res.data.data || res.data;
      this.setState({ courses: Array.isArray(d) ? d : (d.data || []) });
    } catch (e) { /* silent */ }
  };

  handleCourseChange = async (courseId) => {
    if (!courseId) return;
    this.setState({ selectedCourse: courseId, loading: true, enrollments: [], marks: {}, error: null });
    try {
      const res = await api.get("/enrollments", { params: { courseId, limit: 100 } });
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : (d.data || []);
      const marksMap = {};
      list.forEach(e => {
        marksMap[e.id] = { ca1: "", ca2: "", practical: "", exam: "" };
        if (e.courseworkMarks) {
          e.courseworkMarks.forEach(m => { marksMap[e.id][m.component] = String(m.score); });
        }
      });
      this.setState({ enrollments: list, marks: marksMap, loading: false });
    } catch (er) { this.setState({ error: "Failed to load students.", loading: false }); }
  };

  handleMarkChange = (enrollmentId, component) => e => {
    const val = e.target.value;
    this.setState(prev => ({
      marks: Object.assign({}, prev.marks, {
        [enrollmentId]: Object.assign({}, prev.marks[enrollmentId], { [component]: val })
      })
    }));
  };

  handleSave = async () => {
    const { enrollments, marks, selectedCourse } = this.state;
    if (!selectedCourse) return;
    this.setState({ saving: true, error: null });
    try {
      const entries = enrollments.map(e => ({
        enrollmentId: e.id,
        marks: marks[e.id] || {}
      }));
      await api.post("/results/coursework/bulk", { courseId: selectedCourse, entries });
      this.setState({ success: "Marks saved successfully!", saving: false });
      setTimeout(() => this.setState({ success: null }), 3000);
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.message) || "Failed to save marks.";
      this.setState({ error: msg, saving: false });
    }
  };

  render() {
    const { classes } = this.props;
    const { courses, selectedCourse, enrollments, marks, loading, saving, error, success, searchValue } = this.state;

    const computeTotal = e => {
      const m = marks[e.id] || {};
      const ca1 = parseFloat(m.ca1) || 0;
      const ca2 = parseFloat(m.ca2) || 0;
      const practical = parseFloat(m.practical) || 0;
      const exam = parseFloat(m.exam) || 0;
      return Math.min(Math.round((ca1 * 0.3) + (ca2 * 0.2) + (practical * 0.1) + (exam * 0.4)), 100);
    };

    const scoreColumn = (id, header, component) => ({
      id,
      header,
      cell: info => {
        const e = info.row.original;
        const m = marks[e.id] || {};
        return (
          <input
            className={classes.scoreInput}
            type="number"
            min="0"
            max="100"
            value={m[component] || ""}
            onChange={this.handleMarkChange(e.id, component)}
          />
        );
      }
    });

    const columns = [
      { id: "index", header: "#", accessorFn: (row, i) => i + 1, meta: { width: 50 } },
      { 
        id: "student", 
        header: "STUDENT NAME / REG NO.", 
        cell: info => {
          const e = info.row.original;
          return (
            <div>
              <div style={{ fontWeight: 600, color: "#1e293b" }}>{`${e.student?.firstName || ""} ${e.student?.lastName || ""}`}</div>
              <div style={{ fontSize: "11px", color: "#94a3b8" }}>{e.student?.studentNo || "—"}</div>
            </div>
          );
        }
      },
      scoreColumn("ca1", "CA1 (30%)", "ca1"),
      scoreColumn("ca2", "CA2 (20%)", "ca2"),
      scoreColumn("practical", "PRACTICAL (10%)", "practical"),
      scoreColumn("exam", "EXAM (40%)", "exam"),
      {
        id: "total",
        header: "TOTAL /100",
        accessorFn: computeTotal,
        cell: info => <span style={{ fontWeight: 700, color: "#1e293b" }}>{info.getValue() || 0}.0</span>
      },
      {
        id: "grade",
        header: "GRADE",
        cell: info => {
          const total = info.row.getValue("total");
          const { grade, tone } = calcGrade(total);
          return <Pill tone={tone}>{grade}</Pill>;
        }
      },
      {
        id: "status",
        header: "STATUS",
        cell: info => {
          const m = marks[info.row.original.id] || {};
          const isComplete = m.ca1 && m.ca2 && m.practical && m.exam;
          return <Pill tone={isComplete ? "success" : "warning"}>{isComplete ? "Completed" : "In Progress"}</Pill>;
        }
      }
    ];

    const currentCourse = courses.find(c => c.id === selectedCourse);
    const marksEntered = enrollments.filter(e => {
      const m = marks[e.id] || {};
      return m.ca1 && m.ca2 && m.practical && m.exam;
    }).length;
    const completionPercent = enrollments.length ? Math.round((marksEntered / enrollments.length) * 100) : 0;
    
    let avg = 0;
    if (enrollments.length) {
      const totalSum = enrollments.reduce((acc, e) => acc + computeTotal(e), 0);
      avg = (totalSum / enrollments.length).toFixed(1);
    }

    const distData = { A: 0, B: 0, C: 0, D: 0, F: 0 };
    enrollments.forEach(e => {
      const { grade } = calcGrade(computeTotal(e));
      distData[grade] = (distData[grade] || 0) + 1;
    });

    const pieData = {
      series: Object.values(distData)
    };

    return (
      <div style={{ padding: "0 0 40px" }}>
        <div className={classes.heroWrap}>
          <div className={classes.heroOverlay} />
          <div className={classes.heroContent}>
            <div className={classes.backLink} onClick={() => this.props.history.push("/bwillis/courses")}>
              <Icon fontSize="small" style={{ marginRight: "4px" }}>arrow_back</Icon> Back to Courses
            </div>
            
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
              <div>
                <h1 className={classes.heroTitle}>Coursework Marks Entry</h1>
                <p className={classes.heroSub}>
                  CA1 (30%) • CA2 (20%) • Practical (10%) • Exam (40%) = 100%
                </p>
              </div>
              
              <FormControl className={classes.coursePicker} style={{ minWidth: "280px" }}>
                <InputLabel>Course</InputLabel>
                <Select value={selectedCourse} onChange={e => this.handleCourseChange(e.target.value)} disableUnderline>
                  <MenuItem value="">— Select Course —</MenuItem>
                  {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} - {c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </div>
          </div>
        </div>

        {selectedCourse && (
          <>
            <div className={classes.kpiRow}>
              <div className={classes.statCard}>
                <div className={classes.statHeader}>
                  <div className={classes.iconBox} style={{ background: "#eef2ff", color: "#6366f1" }}>
                    <Icon>people</Icon>
                  </div>
                  <div>
                    <h3 className={classes.statNumber}>{enrollments.length}</h3>
                    <p className={classes.statLabel}>Students</p>
                  </div>
                </div>
                <p className={classes.statMeta}>Enrolled</p>
              </div>

              <div className={classes.statCard}>
                <div className={classes.statHeader}>
                  <div className={classes.iconBox} style={{ background: "#ecfdf5", color: "#10b981" }}>
                    <Icon>assignment_turned_in</Icon>
                  </div>
                  <div>
                    <h3 className={classes.statNumber}>{marksEntered}</h3>
                    <p className={classes.statLabel}>Marks Entered</p>
                  </div>
                </div>
                <div>
                  <div className={classes.progressBar}><div className={classes.progressFill} style={{ width: `${completionPercent}%`, background: "#10b981" }} /></div>
                  <p className={classes.statMeta} style={{ marginTop: "4px" }}>{completionPercent}% Completed</p>
                </div>
              </div>

              <div className={classes.statCard}>
                <div className={classes.statHeader}>
                  <div className={classes.iconBox} style={{ background: "#fffbeb", color: "#f59e0b" }}>
                    <Icon>pending_actions</Icon>
                  </div>
                  <div>
                    <h3 className={classes.statNumber}>0</h3>
                    <p className={classes.statLabel}>Pending Review</p>
                  </div>
                </div>
                <p className={classes.statMeta}>0%</p>
              </div>

              <div className={classes.statCard}>
                <div className={classes.statHeader}>
                  <div className={classes.iconBox} style={{ background: "#fef2f2", color: "#ef4444" }}>
                    <Icon>report_problem</Icon>
                  </div>
                  <div>
                    <h3 className={classes.statNumber}>0</h3>
                    <p className={classes.statLabel}>Flagged Marks</p>
                  </div>
                </div>
                <p className={classes.statMeta}>Need Attention</p>
              </div>

              <div className={classes.statCard}>
                <div className={classes.statHeader}>
                  <div className={classes.iconBox} style={{ background: "#f5f3ff", color: "#8b5cf6" }}>
                    <Icon>analytics</Icon>
                  </div>
                  <div>
                    <h3 className={classes.statNumber}>{avg}</h3>
                    <p className={classes.statLabel}>Class Average</p>
                  </div>
                </div>
                <p className={classes.statMeta}>Overall</p>
              </div>
            </div>

            <div style={{ padding: "0 20px" }}>
              {error && <div className={classes.errorMsg}>⚠ {error}</div>}
              {success && <div className={classes.successMsg}>✓ {success}</div>}

              <DataTable
                columns={columns}
                data={enrollments}
                isLoading={loading}
                onSearch={val => this.setState({ searchValue: val })}
                searchValue={searchValue}
                toolbar={
                  <div className={classes.tableActions}>
                    <Button className={classNames(classes.actionBtn, classes.outlineBtn)}>
                      <Icon fontSize="small">upload</Icon> Import Marks
                    </Button>
                    <Button className={classNames(classes.actionBtn, classes.outlineBtn)}>
                      <Icon fontSize="small">download</Icon> Export
                    </Button>
                    <Button 
                      className={classNames(classes.actionBtn, classes.saveBtn)} 
                      onClick={this.handleSave}
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={18} color="inherit" /> : <><Icon fontSize="small">save</Icon> Save All Changes</>}
                    </Button>
                  </div>
                }
              />

              <GridContainer className={classes.bottomGrid}>
                <GridItem xs={12} md={4}>
                  <div className={classes.sectionCard}>
                    <h4 className={classes.sectionTitle}>Mark Distribution</h4>
                    <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
                      <div style={{ width: "120px" }}>
                        <ChartistGraph
                          data={pieData}
                          type="Pie"
                          options={{ donut: true, donutWidth: 20, showLabel: false }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className={classes.distRow}>
                          <div className={classes.distLabel}><div className={classes.dot} style={{ background: "#10b981" }} /> A (80 - 100)</div>
                          <div className={classes.distValue}>{distData.A} ({((distData.A / enrollments.length) * 100).toFixed(1)}%)</div>
                        </div>
                        <div className={classes.distRow}>
                          <div className={classes.distLabel}><div className={classes.dot} style={{ background: "#3b82f6" }} /> B (70 - 79)</div>
                          <div className={classes.distValue}>{distData.B} ({((distData.B / enrollments.length) * 100).toFixed(1)}%)</div>
                        </div>
                        <div className={classes.distRow}>
                          <div className={classes.distLabel}><div className={classes.dot} style={{ background: "#94a3b8" }} /> C (60 - 69)</div>
                          <div className={classes.distValue}>{distData.C} ({((distData.C / enrollments.length) * 100).toFixed(1)}%)</div>
                        </div>
                        <div className={classes.distRow}>
                          <div className={classes.distLabel}><div className={classes.dot} style={{ background: "#f59e0b" }} /> D (50 - 59)</div>
                          <div className={classes.distValue}>{distData.D} ({((distData.D / enrollments.length) * 100).toFixed(1)}%)</div>
                        </div>
                        <div className={classes.distRow}>
                          <div className={classes.distLabel}><div className={classes.dot} style={{ background: "#ef4444" }} /> F (0 - 49)</div>
                          <div className={classes.distValue}>{distData.F} ({((distData.F / enrollments.length) * 100).toFixed(1)}%)</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </GridItem>
                
                <GridItem xs={12} md={4}>
                  <div className={classes.sectionCard}>
                    <h4 className={classes.sectionTitle}>Quick Actions</h4>
                    <div className={classes.quickGrid}>
                      <div className={classes.quickItem}>
                        <Icon className={classes.quickIcon}>dynamic_feed</Icon>
                        <span className={classes.quickTitle}>Bulk Update</span>
                        <span className={classes.quickDesc}>Update multiple marks</span>
                      </div>
                      <div className={classes.quickItem}>
                        <Icon className={classes.quickIcon}>trending_up</Icon>
                        <span className={classes.quickTitle}>Apply Curve</span>
                        <span className={classes.quickDesc}>Adjust class average</span>
                      </div>
                      <div className={classes.quickItem}>
                        <Icon className={classes.quickIcon}>visibility</Icon>
                        <span className={classes.quickTitle}>Flag Review</span>
                        <span className={classes.quickDesc}>Review flagged marks</span>
                      </div>
                      <div className={classes.quickItem}>
                        <Icon className={classes.quickIcon}>check_circle</Icon>
                        <span className={classes.quickTitle}>Mark as Complete</span>
                        <span className={classes.quickDesc}>Submit for review</span>
                      </div>
                    </div>
                  </div>
                </GridItem>

                <GridItem xs={12} md={4}>
                  <div className={classes.sectionCard}>
                    <h4 className={classes.sectionTitle}>Grade Scale</h4>
                    {[
                      { g: "A", range: "80 - 100", label: "Excellent" },
                      { g: "B", range: "70 - 79", label: "Very Good" },
                      { g: "C", range: "60 - 69", label: "Average" },
                      { g: "D", range: "50 - 59", label: "Below Average" },
                      { g: "F", range: "0 - 49", label: "Fail" }
                    ].map(item => (
                      <div key={item.g} className={classes.scaleItem}>
                        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                          <Pill tone={calcGrade(parseFloat(item.range)).tone}>{item.g}</Pill>
                          <span style={{ color: "#64748b" }}>{item.range}</span>
                        </div>
                        <span style={{ color: "#94a3b8" }}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </GridItem>
              </GridContainer>
            </div>
          </>
        )}
      </div>
    );
  }
}

CourseworkMarks.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(CourseworkMarks));
